import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./CreatePassword.scss";
import Input from "../../Components/Common/Input/Input";
import Button from "../../Components/Common/Button/Button";
import { post } from "../../utils/api/apiCaller";
import { ENDPOINTS } from "../../utils/api/endpoints";
import { PASSWORD_REGEX } from "../../utils/Common/Constant";
import OpenEye from "../../Assets/Img/OpenEye";
import CloseEye from "../../Assets/Img/CloseEye";

export default function ResetPassword() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [apiError, setApiError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess] = useState(false);

  const validatePassword = (value: string): string => {
    if (!value) return "Password is required.";
    if (value.length < 12) return "Password must be at least 12 characters.";
    if (value.length > 128) return "Password must be at most 128 characters.";
    if (!PASSWORD_REGEX.test(value))
      return "Password must contain at least one uppercase letter, one lowercase letter, and one number.";
    return "";
  };

  const handlePasswordChange = (val: string) => {
    setPassword(val);
    setPasswordError(val ? validatePassword(val) : "");
    if (confirmPassword)
      setConfirmError(val !== confirmPassword ? "Passwords do not match." : "");
  };

  const handleConfirmChange = (val: string) => {
    setConfirmPassword(val);
    setConfirmError(val && val !== password ? "Passwords do not match." : "");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError("");
    const pwErr = validatePassword(password);
    if (pwErr) {
      setPasswordError(pwErr);
      return;
    }
    if (!confirmPassword) {
      setConfirmError("Please confirm your password.");
      return;
    }
    if (password !== confirmPassword) {
      setConfirmError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    const { data, error } = await post<{ success: boolean; message?: string }>(
      ENDPOINTS.AUTH.RESET_PASSWORD,
      { newPassword: password },
      { "x-reset-token": token },
    );
    setSubmitting(false);

    if (error || !data?.success) {
      setApiError(
        data?.message ??
          error?.message ??
          "Failed to reset password. Please try again.",
      );
      return;
    }
    setSuccess(true);
  };

  if (!token) {
    return (
      <div className="createPasswordScreen">
        <div className="cpStatusBox">
          <h2>Invalid Link</h2>
          <p className="cpErrorText">
            This password reset link is invalid or has expired. Please request a
            new one.
          </p>
          <Button
            className="createBtn cpStatusBtn"
            onClick={() => navigate("/forgot-password", { replace: true })}
          >
            Request New Link
          </Button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="createPasswordScreen">
        <div className="cpStatusBox">
          <h2>Password Reset</h2>
          <p className="cpStatusText">
            Your password has been reset successfully. You can now sign in with
            your new password.
          </p>
          <Button
            className="createBtn cpStatusBtn"
            onClick={() => navigate("/", { replace: true })}
          >
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="createPasswordScreen">
      <div className="container">
        <div className="page">
          <section className="hero">
            <div className="hero-copy">
              <h1>Reset Your Password</h1>
              <p>
                Choose a strong new password to protect your CSC Construction
                account.
              </p>
            </div>
          </section>

          <section className="createPanel">
            <h2>New Password</h2>
            <p>Enter and confirm your new password below.</p>
            <form onSubmit={handleSubmit} noValidate>
              <div className="field">
                <label htmlFor="rp-password">Password</label>
                <div className="pwWrap">
                  <Input
                    id="rp-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    disabled={submitting}
                    className={passwordError ? "inputError" : ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handlePasswordChange(e.target.value)
                    }
                    icon={
                      password ? (
                        !showPassword ? (
                          <CloseEye />
                        ) : (
                          <OpenEye />
                        )
                      ) : null
                    }
                    onIconClick={() => setShowPassword((v) => !v)}
                  />
                </div>
                {passwordError && (
                  <p className="fieldError" role="alert">
                    {passwordError}
                  </p>
                )}
              </div>

              <div className="field">
                <label htmlFor="rp-confirm">Confirm Password</label>
                <div className="pwWrap">
                  <Input
                    id="rp-confirm"
                    type={showConfirm ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    disabled={submitting}
                    className={confirmError ? "inputError" : ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleConfirmChange(e.target.value)
                    }
                    icon={
                      confirmPassword ? (
                        !showConfirm ? (
                          <CloseEye />
                        ) : (
                          <OpenEye />
                        )
                      ) : null
                    }
                    onIconClick={() => setShowConfirm((v) => !v)}
                  />
                </div>
                {confirmError && (
                  <p className="fieldError" role="alert">
                    {confirmError}
                  </p>
                )}
              </div>

              {apiError && (
                <p className="formError" role="alert">
                  {apiError}
                </p>
              )}

              <Button type="submit" className="createBtn" disabled={submitting}>
                {submitting ? (
                  <>
                    <span className="btnSpinner" aria-hidden="true" />
                    Resetting...
                  </>
                ) : (
                  "Reset Password"
                )}
              </Button>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}
